<template>
    <!-- Generated CSS (mirrors juniper-api AccordionController::generateAccordionCSS so the
         builder preview matches the published static output) -->
    <div v-if="generatedCss" v-html="'<style>' + generatedCss + '</style>'"></div>
    <!-- Custom CSS -->
    <div v-if="element && element.style && element.style.customCss" v-html="'<style>' + element.style.customCss + '</style>'"></div>

    <div class="sk-element-wrapper accordion-element" :class="element.id + '-wrapper'" v-if="element.props" @dblclick.stop="onClick">
        <div v-if="element.props.items && element.props.items.length > 0" class="twpb-accordion" :class="[element.props.themeStyle]" :id="element.id">
            <div class="twpb-accordion-item" v-for="(item, index) in element.props.items" :key="item.id || index">
                <div class="twpb-accordion-header" :id="`heading-${element.id}-${item.id}`">
                    <button
                        class="twpb-accordion-button"
                        :class="{ 'twpb-collapsed': !item.collapse }"
                        type="button"
                        :aria-expanded="item.collapse ? 'true' : 'false'"
                        :aria-controls="`collapse-${element.id}-${item.id}`"
                        @click="toggleItem(index)"
                    >
                        {{ item.title }}
                    </button>
                </div>
                <div :id="`collapse-${element.id}-${item.id}`" class="twpb-accordion-collapse twpb-collapse" :class="{ 'twpb-show': item.collapse }" :aria-labelledby="`heading-${element.id}-${item.id}`">
                    <div class="twpb-accordion-body" v-html="item.content"></div>
                </div>
            </div>
        </div>

        <!-- Empty state -->
        <div v-else class="empty-placeholder text-muted">
            <font-awesome-icon :icon="['fas', 'chevron-down']" style="font-size: 28px" />
            <p class="mb-0 mt-2">Add accordion items</p>
        </div>
    </div>
</template>
<script>
export default {
    name: "Accordion",
    props: {
        element: {
            required: false,
            type: Object,
        },
    },
    computed: {
        // Mirror of AccordionController::generateAccordionCSS (juniper-api). Styling (colors /
        // font / padding) lives on the BUTTON so hover works and twpb defaults are overridden;
        // the <h2> header only gets margin-bottom:0. Scoped to #elementId, injected as a <style>.
        generatedCss() {
            const id = this.element.id;
            const style = this.element.style || {};
            const props = this.element.props || {};
            let css = "";

            // Item spacing
            css += `#${id} .twpb-accordion-item {`;
            if (style.itemSpacing && style.itemSpacing !== "0px") css += `margin-bottom:${style.itemSpacing};`;
            css += "}";

            // Item divider (not for type-1)
            if (!props.themeStyle || props.themeStyle !== "type-1") {
                css += `#${id} .twpb-accordion-item:not(:first-of-type){border-top:1px solid rgba(0,0,0,.125);}`;
            }

            // Header wrapper — neutralize margin only
            css += `#${id} .twpb-accordion-header{margin-bottom:0;line-height:inherit;}`;
            // Divider between header and the open body
            css += `#${id} .twpb-accordion-collapse{border-top:1px solid rgba(0,0,0,.125);}`;

            const btn = `#${id} .twpb-accordion-button`;
            const fontStack = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';
            const fontFamily = style.fontFamily === "heading" ? `var(--hedings-font, var(--primary-font, ${fontStack}))` : `var(--primary-font, ${fontStack})`;

            // Header button — title font follows the theme font picked in settings
            css += `${btn}{font-family:${fontFamily};font-size:${style.fontSize && style.fontSize !== "0px" ? style.fontSize : "1rem"};`;
            if (style.lineHeight) css += `line-height:${style.lineHeight};`;
            if (style.fontWeight) css += `font-weight:${style.fontWeight};`;
            if (style.color) css += `color:${style.color};`;
            if (style.backgroundColor) css += `background-color:${style.backgroundColor};`;
            // "0px 0px 0px 0px" = unset (SpacingControl default) → keep the twpb theme padding
            if (style.padding && style.padding !== "0px 0px 0px 0px") css += `padding:${style.padding};`;
            css += "}";

            // Keep the same colors in the expanded state (unset → neutralized by scoped CSS)
            css += `${btn}:not(.twpb-collapsed){`;
            if (style.color) css += `color:${style.color};`;
            if (style.backgroundColor) css += `background-color:${style.backgroundColor};`;
            css += "box-shadow:none;}";

            // Hover colors
            if (style.colorHover || style.backgroundColorHover) {
                css += `${btn}:hover{`;
                if (style.colorHover) css += `color:${style.colorHover};`;
                if (style.backgroundColorHover) css += `background-color:${style.backgroundColorHover};`;
                css += "}";
            }

            // Icon glyph via ::after using the FA7 webfont — EXACTLY like the published output
            // (AccordionController), so the chevron sits flush-right & vertically centered, same
            // as the generated static. The hex is used directly (no name map needed).
            const faStack = '"Font Awesome 7 Free","Font Awesome 6 Free","FontAwesome"';
            if (props.iconClose) css += `${btn}.twpb-collapsed::after{content:"\\${props.iconClose}";font-family:${faStack};font-weight:900;}`;
            if (props.iconOpen) css += `${btn}:not(.twpb-collapsed)::after{content:"\\${props.iconOpen}";font-family:${faStack};font-weight:900;}`;

            // Item content (body) — padding + colors
            let bodyCss = "";
            if (style.contentPadding && style.contentPadding !== "0px 0px 0px 0px") bodyCss += `padding:${style.contentPadding};`;
            if (style.contentColor) bodyCss += `color:${style.contentColor};`;
            if (style.contentBackgroundColor) bodyCss += `background-color:${style.contentBackgroundColor};`;
            if (bodyCss) css += `#${id} .twpb-accordion-body{${bodyCss}}`;
            // content text color also overrides headings inside the content
            if (style.contentColor) css += `#${id} .twpb-accordion-body :is(h1,h2,h3,h4,h5,h6){color:${style.contentColor};}`;

            return css;
        },
    },
    methods: {
        onClick() {
            this.$emit("onClick", this.element);
        },
        toggleItem(index) {
            const items = this.element.props && this.element.props.items;
            if (items && items[index]) items[index].collapse = !items[index].collapse;
        },
    },
    emits: ["onClick"],
};
</script>
<style scoped>
.accordion-element .empty-placeholder {
    padding: 30px;
    text-align: center;
    font-size: 14px;
    color: #9ca3af;
}
/* icon (::after) flush-right & vertically centered via flex margin-left:auto — exactly how
   twpb-bootstrap lays it out on the generated static; glyph/content come from generatedCss */
.accordion-element .twpb-accordion-button::after {
    margin-left: auto;
    flex-shrink: 0;
    padding-left: 12px;
    font-family: "Font Awesome 7 Free", "Font Awesome 6 Free", "FontAwesome";
    font-weight: 900;
}
.accordion-element .twpb-accordion-button {
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
    text-align: left;
    padding: 1rem 1.25rem;
    border: 0;
    border-radius: 0;
    /* native <button> has a gray UA background; twpb gives it a transparent bg on static, mirror it */
    background-color: transparent;
    color: inherit;
    cursor: pointer;
    overflow-anchor: none;
    transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.accordion-element .twpb-accordion-button:focus {
    z-index: 3;
    outline: 0;
    box-shadow: none;
}
.accordion-element .twpb-accordion-button:not(.twpb-collapsed) {
    background-color: unset;
    color: unset;
    box-shadow: none;
}
.accordion-element .twpb-accordion-item {
    background-color: #fff;
    border: 1px solid rgba(0, 0, 0, 0.125);
}
.accordion-element .twpb-accordion-item:first-of-type {
    border-top-left-radius: 0.25rem;
    border-top-right-radius: 0.25rem;
}
.accordion-element .twpb-accordion-item:first-of-type .twpb-accordion-button {
    border-top-left-radius: calc(0.25rem - 1px);
    border-top-right-radius: calc(0.25rem - 1px);
}
.accordion-element .twpb-accordion-item:not(:first-of-type) {
    border-top: 0;
}
.accordion-element .twpb-accordion-item:last-of-type {
    border-bottom-right-radius: 0.25rem;
    border-bottom-left-radius: 0.25rem;
}
.accordion-element .twpb-accordion-item:last-of-type .twpb-accordion-button.twpb-collapsed {
    border-bottom-right-radius: calc(0.25rem - 1px);
    border-bottom-left-radius: calc(0.25rem - 1px);
}
.accordion-element .twpb-accordion-header {
    margin-bottom: 0;
}
.accordion-element .twpb-accordion-collapse {
    border-top: 1px solid rgba(0, 0, 0, 0.125);
}
.accordion-element .twpb-accordion-body {
    padding: 1rem 1.25rem;
}
.accordion-element .twpb-collapse:not(.twpb-show) {
    display: none;
}
</style>
